import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";

interface PopupData {
  id: string;
  active: boolean;
  title: string;
  description: string;
  cta_text: string;
  cta_url: string | null;
  cta_action: string;
  delay_seconds: number;
  dismiss_duration: string;
}

interface SitePopupProps {
  onCtaClick: () => void;
}

const DISMISS_KEY = "sangria_popup_dismissed";

export const SitePopup = ({ onCtaClick }: SitePopupProps) => {
  const [popup, setPopup] = useState<PopupData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchPopup = async () => {
      const { data } = await supabase
        .from("cms_popup")
        .select("*")
        .eq("active", true)
        .limit(1)
        .maybeSingle();

      if (!data) return;

      // Check dismiss
      const dismissed = sessionStorage.getItem(DISMISS_KEY);
      if (dismissed === data.id) return;

      const delay = (data.delay_seconds || 0) * 1000;
      setTimeout(() => setIsVisible(true), delay);
      setPopup(data as PopupData);
    };

    fetchPopup();
  }, []);

  const handleDismiss = () => {
    if (popup) {
      sessionStorage.setItem(DISMISS_KEY, popup.id);
    }
    setIsVisible(false);
  };

  const handleCta = () => {
    if (popup?.cta_action === "external" && popup.cta_url) {
      window.open(popup.cta_url, "_blank", "noopener,noreferrer");
    } else if (popup?.cta_action === "internal" && popup.cta_url) {
      window.location.href = popup.cta_url;
    } else {
      onCtaClick();
    }
    handleDismiss();
  };

  if (!popup || !isVisible) return null;

  // Mobile: bottom banner
  if (isMobile) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300">
        <div className="bg-card border-t border-border shadow-lg p-4 pb-6">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Zapri"
          >
            <X className="h-5 w-5" />
          </button>
          <h3 className="text-lg font-bold text-foreground pr-8 mb-1">
            {popup.title}
          </h3>
          {popup.description && (
            <p
              className="text-sm text-muted-foreground mb-3"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(popup.description),
              }}
            />
          )}
          <Button
            onClick={handleCta}
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full"
          >
            {popup.cta_text}
          </Button>
        </div>
      </div>
    );
  }

  // Desktop: centered modal
  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="text-2xl font-bold text-foreground">
          {popup.title}
        </DialogTitle>
        {popup.description && (
          <p
            className="text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(popup.description),
            }}
          />
        )}
        <div className="flex gap-3 mt-2">
          <Button
            onClick={handleCta}
            className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full"
          >
            {popup.cta_text}
          </Button>
          <Button variant="outline" onClick={handleDismiss} className="rounded-full">
            Zapri
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
